import React, { ComponentType, lazy, Suspense } from "react";
import styled from "styled-components";

import { iconSizes } from "../../styles/constants";
import { actionIcons } from "./actions";
import { appBarIcons } from "./appBar";
import { directionIcons } from "./direction";
import { featureIcons } from "./feature";
import { fileIcons } from "./files";
import { objectIcons } from "./objects";
import { statusIcons } from "./status";

/** Icons grouped by what they show, see the README */
export const iconGroups = {
    appBar: appBarIcons,
    files: fileIcons,
    actions: actionIcons,
    direction: directionIcons,
    status: statusIcons,
    feature: featureIcons,
    objects: objectIcons,
};

// Each icon is its own chunk, loaded the first time it renders
export const iconMap = {
    ...iconGroups.appBar,
    ...iconGroups.files,
    ...iconGroups.actions,
    ...iconGroups.direction,
    ...iconGroups.status,
    ...iconGroups.feature,
    ...iconGroups.objects,
};

export type IconName = keyof typeof iconMap;
export type IconSize = keyof typeof iconSizes;

export interface IconProps extends React.DOMAttributes<HTMLSpanElement> {
    icon: IconName;
    /** Rendered width and height, from `iconSizes` */
    size: IconSize;
    /** Icon color, defaults to the surrounding text color */
    color?: string;
    pointer?: boolean;
    [key: string]: any;
}

const lazyIcons = new Map<IconName, ComponentType>();

const getLazyIcon = (icon: IconName) => {
    let component = lazyIcons.get(icon);
    if (!component) {
        component = lazy(iconMap[icon]);
        lazyIcons.set(icon, component);
    }
    return component;
};

const Icon = ({ color, icon, size, pointer, ...otherProps }: IconProps) => {
    if (!iconMap[icon]) {
        return null;
    }
    const LazyIcon = getLazyIcon(icon);
    return (
        <IconWrapper
            $color={color}
            $size={size}
            $pointer={pointer || false}
            {...otherProps}
        >
            {/* The wrapper has its final size, so loading leaves no layout shift */}
            <Suspense fallback={null}>
                <LazyIcon />
            </Suspense>
        </IconWrapper>
    );
};

interface IconFrameProps extends Omit<IconProps, "icon"> {
    glyph: ComponentType;
}

/**
 * Sizes and colours a glyph like <Icon> does, for the few glyphs that are
 * private to one component (see Forms/icons) and not in the registry.
 */
export const IconFrame = ({
    glyph: Glyph,
    color,
    size,
    pointer,
    ...otherProps
}: IconFrameProps) => (
    <IconWrapper
        $color={color}
        $size={size}
        $pointer={pointer || false}
        {...otherProps}
    >
        <Glyph />
    </IconWrapper>
);

interface IconWrapperProps {
    $color?: string;
    $size: IconSize;
    $pointer: boolean;
}
const IconWrapper = styled.span<IconWrapperProps>`
    display: inline-block;
    /* Lay out like a bare svg, without an extra line box below the glyph */
    line-height: 0;
    width: ${(props) => iconSizes[props.$size]};
    height: ${(props) => iconSizes[props.$size]};

    svg {
        width: 100%;
        height: 100%;
    }

    &.flip {
        transform: rotate(180deg);
    }
    &.clickable {
        cursor: pointer;
    }

    color: ${(props) => props.$color || "currentColor"};
    ${(props) => (props.$pointer ? "cursor: pointer;" : "")}
`;

export default Icon;
